import React, { Component, createRef, RefObject } from 'react';
import { ApiUrls } from '@api/apiUrls';
import { SvgIcon } from '@components-library/Icons/SvgIcon';
import { keyMap } from '@locales/lokalise/keyMap';
import { fetchApi } from 'api/fetchApi';
import DeleteIcon from 'assets/icons/trash icon.svg';
import Constructor from 'components/shared/configConstructor/index';
import { CONSTANTS } from 'components/shared/constants';
import ErrorView from 'components/shared/errorView';
import Button from 'components/shared/forms/actionButton';
import { ConfirmDialog } from 'components/shared/modal';
import Preloader from 'components/shared/preloader';
import { showQFormSignature } from 'components/shared/qForm/qFormSignature';
import { EmptyObject } from 'components/shared/shared.interfaces';
import Signature, { SignatureRef } from 'components/shared/signature/index';
import { connectToContext, IAppContext } from 'contextStore';
import { Q_FORM_STATUS } from 'global.enums';
import { SignatureDataType } from 'global.interfaces';
import { handleError } from 'utils/errors';
import { FormDataType, QFormInfo, QuestionsDataType, QuestionsInfoType } from './qforms.types';
import StatusInfo from './statusInfo';

interface IProps extends IAppContext {
  qFormType: string;
  refresh: () => Promise<void>;
  tradingAccountId?: number | string;
  additionalValidation?: () => boolean | null;
  renderAfterComponent?: () => JSX.Element | null;
  onCreateHandler?: (id?: number) => void;
  onSendHandlerSuccess?: () => void;
  onSendHandlerFail?: (errorMessage: string) => void;
  className: string;
  classNameKey: string;
  id?: number;
  status: Q_FORM_STATUS;
  isDisabled?: boolean;
  additionalData?: IAdditionalData;
}

interface IAdditionalData {
  [key: string]: string;
}

interface IState {
  info: QuestionsInfoType | EmptyObject;
  data: QuestionsDataType | EmptyObject;
  signature: SignatureDataType | null;
  processing: boolean;
  isValid: boolean | null | undefined;
  qFormInfo: QFormInfo | EmptyObject;
  errMsg: string;
}

class QForm extends Component<IProps, IState> {
  state: IState = {
    info: {},
    data: {},
    signature: null,
    processing: false,
    isValid: true,
    qFormInfo: {},
    errMsg: '',
  };

  signature: RefObject<SignatureRef> = createRef();

  form: RefObject<Constructor<QuestionsInfoType, QuestionsDataType>> = createRef();

  componentDidMount() {
    const { id } = this.props;

    if (!id) {
      return;
    }

    if (id > 0) {
      void this.get(id);
    }
  }

  get createEndpoint() {
    const { className, qFormType, tradingAccountId } = this.props;

    let createEndpoint = `${ApiUrls.qFormUser.create}?className=${className}&qFormType=${qFormType}`;

    if (tradingAccountId) {
      createEndpoint = `${createEndpoint}&tradingAccountId=${tradingAccountId}`;
    }

    return createEndpoint;
  }

  create = async () => {
    const { refresh, isDisabled, onCreateHandler, additionalData } = this.props;

    if (isDisabled) {
      return;
    }

    const { createEndpoint } = this;

    try {
      const response = await fetchApi.post<number>(createEndpoint, additionalData);

      if (onCreateHandler) {
        onCreateHandler(response.data);
      }

      void refresh();
    } catch (error) {
      handleError(keyMap.partnershipQuestionnaireQuestionnaireCreateFailErr, error);
    }
  };

  get = async (id: number) => {
    this.setState({ processing: true });
    try {
      const response = await fetchApi.get<FormDataType>(`${ApiUrls.qFormUser.get}?id=${id}`);

      const { signature } = response.data.details;
      const { info, data } = response.data.details.questions;
      const { qFormInfo } = response.data;

      this.setState({
        info,
        data,
        signature,
        qFormInfo,
      });

      if (this.signature && this.signature.current) {
        this.signature.current.setData(signature as SignatureDataType);
      }
    } catch (error) {
      this.setState({ errMsg: error.response.data });
      handleError(keyMap.sharedErrorsFailedToGetRecord, error);
    } finally {
      this.setState({ processing: false });
    }
  };

  set = async () => {
    const { id, refresh } = this.props;

    try {
      const formData = this.prepareFormData();

      await fetchApi.post(`${ApiUrls.qFormUser.update}?id=${id}`, formData);
      void refresh();
    } catch (error) {
      handleError(keyMap.partnershipQuestionnaireQuestionnaireUpdateFailErr, error);
    }
  };

  isSignatureValid = (): boolean => {
    const { qFormInfo } = this.state;
    const { type } = qFormInfo;

    if (!showQFormSignature(type)) {
      return true;
    }

    if (!this.signature.current?.validate()) {
      return false;
    }

    return true;
  };

  send = () => {
    const { id, refresh, additionalValidation, onSendHandlerSuccess, onSendHandlerFail } = this.props;

    const isAdditionalValid = additionalValidation ? additionalValidation() : true;
    const isFormValid = this.form.current?.validate();
    const isValid = isFormValid && this.isSignatureValid() && isAdditionalValid;

    this.setState({ isValid });

    if (!isValid) {
      return;
    }

    const formData = this.prepareFormData();

    return fetchApi
      .post(`${ApiUrls.qFormUser.send}?id=${id}`, formData)
      .then(() => {
        if (onSendHandlerSuccess) {
          onSendHandlerSuccess();
        }
        void refresh();
      })
      .catch((error) => {
        if (onSendHandlerFail) {
          onSendHandlerFail(error.response.data);
        }
        handleError(keyMap.partnershipQuestionnaireQuestionnaireSendFailErr, error);
      });
  };

  prepareFormData = () => {
    const { data, signature } = this.state;
    const form = { answers: data };
    const formData = new FormData();

    formData.append('form', JSON.stringify(form));

    if (signature?.data) {
      const { data: byteString, metaInfo: mimeString } = signature;
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      const blob = new Blob([ia], { type: mimeString });

      formData.append('file', blob, `${Date.now()}.png`);
    }

    return formData;
  };

  changeHandler = (data: QuestionsDataType | EmptyObject) => {
    this.setState({ data });
  };

  setHandler = () => this.set();

  changeSignatureHandler = (signature: SignatureDataType | null) => {
    this.setState({ signature });
  };

  delete = () => {
    const { id, refresh } = this.props;

    // @ts-ignore: remove after translating into typescript
    ConfirmDialog.show(keyMap.partnershipQuestionnaireDeleteConfirmationTxt, () =>
      fetchApi
        .post(`${ApiUrls.qFormUser.delete}?id=${id}`)
        .then(() => refresh())
        .catch((error) => handleError(keyMap.partnershipQuestionnaireQuestionnaireDeleteFailErr, error)),
    );
  };

  renderSignature(isDisabledForm: boolean) {
    const { i18next } = this.props;
    const { signature, qFormInfo } = this.state;

    const { type } = qFormInfo;

    if (!showQFormSignature(type)) {
      return;
    }

    return (
      <div className="cc-field signature-field">
        <span className="cc-filed__label">{i18next.t(keyMap.profileSignature)}</span>
        <Signature
          ref={this.signature}
          onChange={this.changeSignatureHandler}
          disabled={isDisabledForm}
          data={signature}
        />
      </div>
    );
  }

  render(): JSX.Element {
    const { processing, errMsg, info, data, isValid, qFormInfo } = this.state;
    const { status, id, isDisabled, renderAfterComponent, classNameKey, i18next } = this.props;

    if (id === 0) {
      return (
        <div className="form-box qform-container__start">
          <div className="qform-start__label">{i18next.t(keyMap.partnershipQuestionnaireCreateQuestionarrieTxt)}</div>
          <Button className="btn" type="button" onClick={this.create} disabled={isDisabled}>
            {i18next.t(keyMap.partnershipQuestionnaireStartBtn)}
          </Button>
        </div>
      );
    }

    if (processing) {
      return <Preloader />;
    }

    if (errMsg) {
      return <ErrorView error={errMsg} />;
    }

    const isDisabledForm =
      [
        CONSTANTS.Q_FORM_STATUS.APPROVED,
        CONSTANTS.Q_FORM_STATUS.COMPLETED,
        CONSTANTS.Q_FORM_STATUS.INPROCESS,
        CONSTANTS.Q_FORM_STATUS.NEW,
        CONSTANTS.Q_FORM_STATUS.TERMINATION_IN_PROGRESS,
      ].indexOf(status) > -1;

    return (
      <React.Fragment>
        <div className="form-box qform-container__info">
          <StatusInfo data={qFormInfo} />
          <button
            type="button"
            className="qform-delete"
            onClick={this.delete}
            disabled={isDisabledForm}
            title={i18next.t(keyMap.sharedActionsDelete)}>
            <SvgIcon src={DeleteIcon} />
          </button>
        </div>

        <div className="cc-form__container member--cc-form">
          <Constructor
            ref={this.form}
            name={classNameKey}
            info={info}
            data={data}
            changeHandler={this.changeHandler}
            disabledForm={isDisabledForm}
            after={(withValidation, withoutValidation) => (
              <React.Fragment>
                {renderAfterComponent && renderAfterComponent()}
                {this.renderSignature(isDisabledForm)}

                <div className="form column--form">
                  <ul className="form-actions">
                    <li>
                      <Button
                        type="button"
                        className="btn"
                        id={`Send_${classNameKey}`}
                        disabled={isDisabledForm}
                        onClick={withoutValidation(this.send)}>
                        {i18next.t(keyMap.partnershipQuestionnaireSendBtn)}
                      </Button>
                    </li>
                    <li>
                      <Button
                        type="button"
                        className="light--button"
                        id={`Update_${classNameKey}`}
                        disabled={isDisabledForm}
                        onClick={withoutValidation(this.setHandler)}>
                        {i18next.t(keyMap.sharedActionsSave)}
                      </Button>
                    </li>
                  </ul>
                  {!isValid && (
                    <div className="qform__error-container">
                      <div className="form__error">{i18next.t(keyMap.partnershipQuestionnaireValidationFailTxt)}</div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            )}
          />
        </div>
      </React.Fragment>
    );
  }
}

export default connectToContext(QForm);
